import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import styles from 'assets/jss/material-dashboard-pro-react/components/prepaidLoyaltyAddNewServicesStyle';
import { DisplayS } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import TabsWithDots from 'components/TabsWithDots/TabsWithDots';
import Button from 'components/CustomButtons/Button';
import NumbersTab from 'views/PrepaidLoyalty/commons/tabs/NumbersTab';
import PaymentMethodTab from 'views/PrepaidLoyalty/commons/tabs/PaymentMethodTab/PaymentMethodTab';
import { PAYMENT_METHODS, DEFAULT_CARD, URL_PATHS } from 'views/PrepaidLoyalty/commons/constants';
import { PeriodUnit, Period } from 'components/CustomRepeatEvery/CustomRepeatEvery';
import DateTab from 'views/PrepaidLoyalty/commons/tabs/DateTab';
import SummaryTab from 'views/PrepaidLoyalty/commons/tabs/SummaryTab';
import moment from 'moment';
import { useHistory } from 'react-router';
import { validateDateStep, validatePaymentMethodStep } from 'views/PrepaidLoyalty/helpers/validateStepHelper';
import { LoyaltyGroupService } from 'services/loyaltyGroups/loyaltyGroups';
import { SingleLoyaltyNumber } from 'utils/api/models/prepaidLoyalty/singleLoyaltyNumber';
import { catchError, concatMap, finalize } from 'rxjs/operators';
import { decode } from 'utils/crypto/crypto';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import NavigationPromptDialog from 'views/TopUpAddOns/commons/NavigationPrompt';
import { throwError } from 'rxjs';
import PaymentService from 'services/payments/payment';
import prepareData from 'views/TopUpAddOns/helpers/prepare';
import { useSelector } from 'react-redux';
import { emailSelector } from 'redux/modules/session/selectors';
import { dispatch } from 'redux/store';
import { setPaymentType } from 'redux/modules/paymentType/actions';
import { setToast } from 'redux/modules/toast/actions';
import useBasics from 'views/PrepaidLoyalty/hooks/useBasics';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import useGetCreditLine from 'views/PrepaidLoyalty/hooks/useGetCreditLine';
import { ITEM_TYPE } from 'views/TopUpAddOns/commons/constants';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import { config } from '../config';
import { SingleBillingNumber } from 'utils/api/models/accounts/singleBillingNumber';
import tabsContainerStyle from 'assets/jss/material-dashboard-pro-react/layouts/tabsContainerStyle';
import { PaymentRedirection } from 'components/PaymentMethod/helper/payment';

// @ts-ignore
const useStyle = makeStyles(styles);
// @ts-ignore
const useTabsContainerStyle = makeStyles(tabsContainerStyle);

const MIN_SEARCH_TEXT_LENGTH = 3;

interface ILoading {
	limit: boolean;
}

enum STEPS {
	PAYMENT_METHOD = 0,
	NUMBERS_TOPUP = 1,
	DATE = 2,
	SUMMARY = 3,
}

interface IAddNewTopUp {
	addSubscription: (
		groupId: number,
		isTopUp: boolean,
		topUpValue: number,
		subscriptionList: string[],
		numbers: string[]
	) => void;
	addSubscriptionError: () => void;
	setPaymentDialog: React.Dispatch<React.SetStateAction<types.topUpAddOns.paymentDialog>>;
	setShowPaymentFrame: React.Dispatch<React.SetStateAction<boolean>>;
	match: {
		path: string;
		params: {
			encodedGroupId: string;
		};
	};
}

const AddNewTopUp = ({ match, addSubscription, addSubscriptionError, setPaymentDialog }: IAddNewTopUp) => {
	const classes = useStyle();
	const tabsContainerClasses = useTabsContainerStyle();
	const [activeTab, setActiveTab] = useState<number>(STEPS.PAYMENT_METHOD);
	const [numbersLoading, setNumbersLoading] = useState<boolean>(false);
	const [loading, setLoading] = useState<ILoading>({
		limit: false,
	});
	const [groupNumbers, setGroupNumbers] = useState<SingleLoyaltyNumber[]>([]);
	const [selectedNumbers, setSelectedNumbers] = useState<SingleLoyaltyNumber[]>([]);
	const [topUpValue, setTopUpValue] = useState<number>(3);
	const [masterNumber, setMasterNumber] = useState<string>('');
	const [numbersFromMasterSearch, setNumbersFromMasterSearch] = useState<SingleBillingNumber[]>([]);
	const [accountNumber, setAccountNumber] = useState<string>('');
	const [accountDetails, setAccountDetails] = useState<string>('');
	const [userData, setUserData] = useState<CorpUserData | undefined>();
	const topupRef = useRef<boolean>(true);
	const [paymentMethod, setPaymentMethod] = useState<number>(PAYMENT_METHODS.BY_MASTER_NUMBER);
	const [creditCardData, setCreditCardData] = useState<types.topUpAddOns.IFormData>(DEFAULT_CARD);
	const [saveCreditCard, setSaveCreditCard] = useState<boolean>(false);
	const [isRepeatEnabled, setIsRepeatEnabled] = useState<boolean>(false);
	const [selectedPeriod, setSelectedPeriod] = useState<PeriodUnit>(PeriodUnit.MONTH);
	const [periodicity, setPeriodicity] = useState<string>('1');
	const [period, setPeriod] = useState<Period>({
		startDate: moment().startOf('day'),
		endDate: null,
	});
	const [repeatPeriod, setRepeatPeriod] = useState<number>(0);
	const [confirmingPayment, setConfirmingPayment] = useState<boolean>(false);
	const [validation, setValidation] = useState<types.topUpAddOns.IValidation>({
		number: { isValid: true, isPotentiallyValid: true, card: null },
		cvv: { isValid: true, isPotentiallyValid: true },
		date: { isValid: true, isPotentiallyValid: true },
	});
	const [paymentLimitReached, setPaymentLimitReached] = useState<types.topUpAddOns.paymentLimitReached>();
	const [checkValidity, setCheckValidity] = useState<boolean>(false);
	const history = useHistory();
	const allowedPeriods = [PeriodUnit.DAY, PeriodUnit.WEEK, PeriodUnit.MONTH];
	const groupId = Number(decode(match.params.encodedGroupId));
	const email = useSelector(emailSelector);
	useEffect(() => {
		dispatch(setPaymentType({ action: 'TOP_UP' }));
	}, []);
	const [numberOfOrders] = useBasics(period, periodicity, selectedPeriod);

	const totalAmount = useMemo(() => {
		return selectedNumbers.length * topUpValue * numberOfOrders;
	}, [selectedNumbers, topUpValue, numberOfOrders]);

	const phoneNumber: string = useMemo(() => {
		if (masterNumber.length > MIN_SEARCH_TEXT_LENGTH) {
			return masterNumber;
		}
		return '';
	}, [masterNumber]);

	const [creditLimit, setCreditLimit, balance] = useGetCreditLine(
		phoneNumber,
		accountNumber,
		masterNumber,
		setLoading,
		dispatch,
		topupRef
	);

	const payByCreditCard = () => {
		setConfirmingPayment(true);
		const paymentResponse = LoyaltyGroupService.addSubscription({
			groupId,
			numbers: selectedNumbers.map(({ number }) => number),
			date: period.startDate.format('YYYY-MM-DDTHH:mm:ss'),
			value: topUpValue,
			repeat: isRepeatEnabled
				? {
						startDate: period.startDate.format('YYYY-MM-DDTHH:mm:ss'),
						endDate: period.endDate?.format('YYYY-MM-DDTHH:mm:ss'),
						recurrenceType: selectedPeriod,
						recurrenceNumber: periodicity,
				  }
				: undefined,
		}).pipe(
			concatMap(({ response }) => {
				const numbers = selectedNumbers.map(({ number }) => number);
				const subscriptionId = response.id;
				const data = prepareData(
					validation,
					creditCardData,
					numbers,
					topUpValue,
					email,
					subscriptionId,
					totalAmount,
					ITEM_TYPE.TOP_UP,
					numberOfOrders
				);
				return PaymentService.makePayment(data);
			}),
			catchError((error) => throwError(error))
		);
		paymentResponse
			.pipe(
				finalize(() => {
					setConfirmingPayment(false);
				})
			)
			.subscribe(
				() => {
					history.push('?result=success');
				},
				(error) => {
					switch (error?.response?.code) {
						case 'PAYER_AUTHENTICATION_REQUIRED':
							dispatch(setPaymentType({ action: 'TOP_UP' }));
							PaymentRedirection(error?.response?.url);
							break;

						case 'CREDIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_DONATION_AMOUNT':
							setPaymentLimitReached('credit');
							break;

						case 'DEBIT_CARD_PAYMENT_REQUEST_EXCEEDS_MAX_TOPUP_AMOUNT':
							setPaymentLimitReached('debit');
							break;

						case 'TRANSACTION_REJECTED':
							setPaymentDialog('paymentRejected');
							break;

						default:
							dispatch(
								setToast({
									error: true,
									label: translate('error.occured'),
									actionType: translate('error'),
								})
							);
							break;
					}
				}
			);
	};

	const payByMasterNumber = () => {
		const numbers = selectedNumbers.map(({ number }) => number);
		setConfirmingPayment(true);
		LoyaltyGroupService.addSubscription({
			msisdn: masterNumber,
			groupId,
			numbers,
			date: period.startDate.format('YYYY-MM-DDTHH:mm:ss'),
			value: topUpValue,
			repeat: isRepeatEnabled
				? {
						startDate: period.startDate.format('YYYY-MM-DDTHH:mm:ss'),
						endDate: period.endDate?.format('YYYY-MM-DDTHH:mm:ss'),
						recurrenceType: selectedPeriod,
						recurrenceNumber: periodicity,
				  }
				: undefined,
		}).subscribe(
			() => {
				setSelectedNumbers([]);
				setConfirmingPayment(false);
				history.push(`/${URL_PATHS.PREPAID_LOYALTY_ROOT}`);
				addSubscription(groupId, true, topUpValue, [], numbers);
			},
			(error) => {
				setSelectedNumbers([]);
				setConfirmingPayment(false);
				history.push(`/${URL_PATHS.PREPAID_LOYALTY_ROOT}`);
				addSubscriptionError();
			}
		);
	};

	const confirmPayment = () => {
		if (paymentMethod === PAYMENT_METHODS.BY_MASTER_NUMBER) {
			payByMasterNumber();
		} else if (paymentMethod === PAYMENT_METHODS.BY_CREDIT_CARD) {
			payByCreditCard();
		}
	};

	const nextFn = () => {
		switch (activeTab) {
			case STEPS.SUMMARY:
				isStepValid && confirmPayment();
				break;
			case STEPS.PAYMENT_METHOD:
				if (paymentMethod === PAYMENT_METHODS.BY_CREDIT_CARD) {
					setCheckValidity(true);
				}
				if (isStepValid) {
					setActiveTab((activeTab) => activeTab + 1);
				}
				break;
			default:
				if (isStepValid) {
					setActiveTab((activeTab) => activeTab + 1);
				}
				break;
		}
	};

	const backFn = () => {
		switch (activeTab) {
			case STEPS.PAYMENT_METHOD:
				history.push('/dashboard/gift-program');
				break;
			default:
				setActiveTab((activeTab) => activeTab - 1);
				break;
		}
	};

	useEffect(() => {
		if (Object.values(loading).every((value) => !value)) {
			dispatch(hideLoadingIndicator());
		} else {
			dispatch(showLoadingIndicator());
		}
	}, [loading]);

	useEffect(() => {
		if (groupId) {
			setNumbersLoading(true);
			const subscription = LoyaltyGroupService.getLoyaltyGroupDetails(groupId)
				.pipe(
					finalize(() => {
						setNumbersLoading(false);
					})
				)
				.subscribe((loyaltyGroupDetails) => {
					setGroupNumbers(loyaltyGroupDetails.numbers);
					setSelectedNumbers(loyaltyGroupDetails.numbers);
				});
			return () => {
				subscription.unsubscribe();
			};
		}
	}, [groupId]);

	useEffect(() => {
		if (groupNumbers && selectedNumbers) {
			if (
				paymentMethod === PAYMENT_METHODS.BY_CREDIT_CARD &&
				selectedNumbers?.length >= config.MAX_NUMBERS_CC_PAYMENT
			) {
				setSelectedNumbers(selectedNumbers?.slice(0, config.MAX_NUMBERS_CC_PAYMENT));
			} else {
				setSelectedNumbers(groupNumbers);
			}
		}
	}, [paymentMethod, selectedNumbers, groupNumbers]);

	const isNumberSelected = useCallback(
		(number: SingleLoyaltyNumber) => {
			return !!selectedNumbers.find((num: SingleLoyaltyNumber) => num.id === number.id);
		},
		[selectedNumbers]
	);

	const changeSelected = useCallback(
		(number: SingleLoyaltyNumber) => {
			const checked = isNumberSelected(number);
			if (checked) {
				return setSelectedNumbers((selectedNums: SingleLoyaltyNumber[]) =>
					selectedNums.filter((selectedNumber: SingleLoyaltyNumber) => selectedNumber.id !== number.id)
				);
			} else {
				return setSelectedNumbers((selectedNums: SingleLoyaltyNumber[]) => selectedNums.concat(number));
			}
		},
		[isNumberSelected]
	);

	const setCreditCardSingleField: types.topUpAddOns.setFormData = useCallback(
		(name: keyof types.topUpAddOns.IFormData, value: string | boolean) =>
			setCreditCardData((formData) => ({ ...formData, [name]: value })),
		[]
	);

	const isStepValid: boolean = useMemo(() => {
		switch (activeTab) {
			case STEPS.PAYMENT_METHOD:
				return validatePaymentMethodStep(paymentMethod, masterNumber, creditCardData);
			case STEPS.NUMBERS_TOPUP:
				return (
					(paymentMethod === PAYMENT_METHODS.BY_CREDIT_CARD &&
						selectedNumbers.length <= config.MAX_NUMBERS_CC_PAYMENT) ||
					(paymentMethod === PAYMENT_METHODS.BY_MASTER_NUMBER && selectedNumbers.length > 0)
				);
			case STEPS.DATE:
				return validateDateStep(period, isRepeatEnabled, periodicity, selectedPeriod);
			case STEPS.SUMMARY:
				return !confirmingPayment;
			default:
				return false;
		}
	}, [
		activeTab,
		creditCardData,
		isRepeatEnabled,
		masterNumber,
		paymentMethod,
		period,
		periodicity,
		selectedNumbers.length,
		selectedPeriod,
		confirmingPayment,
	]);

	const nextButtonDisabled = useMemo(() => {
		if (activeTab === STEPS.PAYMENT_METHOD && paymentMethod === PAYMENT_METHODS.BY_CREDIT_CARD) {
			return checkValidity && !isStepValid;
		}
		return !isStepValid;
	}, [isStepValid, activeTab, checkValidity, paymentMethod]);

	const toggleSaveCreditCard = () => {
		setSaveCreditCard((value) => !value);
	};

	return (
		<div className={tabsContainerClasses.container}>
			<ConditionalRender show={selectedNumbers.length > 0} onTrue={() => <NavigationPromptDialog />} />
			<DisplayS color="secondary-light">{translate('prepaid.loyalty.add.new.topUp.title')}</DisplayS>
			<TabsWithDots
				tabs={[
					{
						name: translate('prepaid.loyalty.add.new.services.payment.method.tab'),
						content: (
							<PaymentMethodTab
								paymentMethod={paymentMethod}
								creditCardData={creditCardData}
								creditLimit={creditLimit}
								setCreditLimit={setCreditLimit}
								balance={balance}
								setAccountNumber={setAccountNumber}
								onPaymentMethodChange={setPaymentMethod}
								onCreditCardDataChange={setCreditCardSingleField}
								masterNumber={masterNumber}
								setMasterNumber={setMasterNumber}
								numbersFromMasterSearch={numbersFromMasterSearch}
								setNumbersFromMasterSearch={setNumbersFromMasterSearch}
								validation={validation}
								setValidation={setValidation}
								paymentLimitReached={paymentLimitReached}
								checkValidity={checkValidity}
								setAccountDetails={setAccountDetails}
								setUserData={setUserData}
								saveCreditCard={saveCreditCard}
								toggleSaveCreditCard={toggleSaveCreditCard}
							/>
						),
					},
					{
						name: translate('prepaid.loyalty.add.new.topUp.numbers.tab'),
						content: (
							<NumbersTab
								numbersLoading={numbersLoading}
								selectedNumbers={selectedNumbers}
								numbersData={groupNumbers}
								changeSelected={changeSelected}
								isNumberSelected={isNumberSelected}
								topUpValue={topUpValue}
								setTopUpValue={setTopUpValue}
								creditLimit={creditLimit}
								setCreditLimit={setCreditLimit}
								paymentMethod={paymentMethod}
								setActiveTab={setActiveTab}
								masterNumber={masterNumber}
								accountDetails={accountDetails}
								accountNumber={accountNumber}
								userData={userData}
							/>
						),
					},
					{
						name: translate('prepaid.loyalty.add.new.services.date.tab'),
						content: (
							<DateTab
								selectedPeriod={selectedPeriod}
								setSelectedPeriod={setSelectedPeriod}
								periodicity={periodicity}
								setPeriodicity={setPeriodicity}
								period={period}
								setPeriod={setPeriod}
								isRepeatEnabled={isRepeatEnabled}
								setIsRepeatEnabled={setIsRepeatEnabled}
								allowedPeriods={allowedPeriods}
								numberOfOrders={numberOfOrders}
								repeatPeriod={repeatPeriod}
								setRepeatPeriod={setRepeatPeriod}
							/>
						),
					},
					{
						name: translate('prepaid.loyalty.add.new.services.summary.tab'),
						content: (
							<SummaryTab
								paymentMethod={paymentMethod}
								selectedNumbersAmount={selectedNumbers.length}
								masterNumber={masterNumber}
								creditCardNumber={creditCardData.number}
								totalAmount={totalAmount}
								isRepeatEnabled={isRepeatEnabled}
								selectedPeriod={selectedPeriod}
								periodicity={periodicity}
								period={period}
								confirmingPayment={confirmingPayment}
							/>
						),
					},
				]}
				activeTab={activeTab}
				setActiveTab={setActiveTab}
			/>
			<div
				className={classNames({
					[classes.tabsButtons]: true,
					[classes.tabsButtonsIn]:
						activeTab === STEPS.DATE || activeTab === STEPS.PAYMENT_METHOD || activeTab === STEPS.SUMMARY,
				})}
			>
				<div className={classNames(classes.right)}>
					<Button
						color="primary-light"
						className={classNames({
							[classes.nextButton]: activeTab !== STEPS.SUMMARY,
							[classes.confirmPaymentButton]: activeTab === STEPS.SUMMARY,
						})}
						disabled={nextButtonDisabled}
						onClick={nextFn}
					>
						{translate(
							activeTab === STEPS.SUMMARY ? 'prepaid.loyalty.add.new.services.confirm.payment' : 'next'
						)}
					</Button>
				</div>
				<div className={classNames(classes.right)}>
					<Button
						color="tertiary-light"
						onClick={backFn}
						disabled={activeTab === STEPS.SUMMARY && confirmingPayment}
					>
						{translate(activeTab === STEPS.PAYMENT_METHOD ? 'cancel' : 'back')}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default AddNewTopUp;
